import {
  CheckCircle,
  Clock,
  CreditCard,
  Spinner,
} from "@phosphor-icons/react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { httpService } from "../../services/httpService";
import { API_CONFIG } from "../../shared/constants";
import { ISubscriptionDetails } from "../../interfaces";
import { useVerifyRazorpayPayment } from "../../hooks";

type VerifyStatus = "verifying" | "success" | "processing";

const MAX_RETRIES = 4;
const RETRY_DELAY_MS = 3000;
const REDIRECT_DELAY_MS = 4000;

const PaymentVerify: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const razorpayPaymentId = searchParams.get("razorpay_payment_id");
  const razorpaySubscriptionId = searchParams.get("razorpay_subscription_id");
  const razorpaySignature = searchParams.get("razorpay_signature");
  const isRazorpay = !!razorpayPaymentId && !!razorpaySubscriptionId && !!razorpaySignature;

  const [status, setStatus] = useState<VerifyStatus>("verifying");
  const [countdown, setCountdown] = useState(REDIRECT_DELAY_MS / 1000);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const verifyCalledRef = useRef(false);

  const verifyRazorpayMutation = useVerifyRazorpayPayment();

  const pollForActivation = () => {
    const checkPlan = async () => {
      try {
        const res = await httpService.get<ISubscriptionDetails>(
          API_CONFIG.path.stripeDetails
        );
        if (res.plan !== null && res.plan !== undefined) {
          setStatus("success");
          return;
        }
      } catch {
        // Network error — still retry; don't bail early
      }

      retryRef.current += 1;
      if (retryRef.current < MAX_RETRIES) {
        timerRef.current = setTimeout(checkPlan, RETRY_DELAY_MS);
      } else {
        setStatus("processing");
      }
    };
    checkPlan();
  };

  useEffect(() => {
    if (!sessionId && !isRazorpay) {
      navigate("/pricing", { replace: true });
      return;
    }

    if (isRazorpay) {
      if (verifyCalledRef.current) return;
      verifyCalledRef.current = true;

      verifyRazorpayMutation.mutate(
        {
          razorpay_payment_id: razorpayPaymentId!,
          razorpay_subscription_id: razorpaySubscriptionId!,
          razorpay_signature: razorpaySignature!,
        },
        {
          onSuccess: () => pollForActivation(),
          onError: () => pollForActivation(),
        }
      );
      return;
    }

    pollForActivation();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-redirect countdown on success
  useEffect(() => {
    if (status !== "success") return;

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          navigate("/dashboard", { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [status, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-neutral-800 rounded-2xl shadow-sm border border-gray-200 dark:border-neutral-700 p-8">
        {/* Brand */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            <CreditCard size={24} className="text-indigo-600" weight="duotone" />
            <span className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              {t("common.appName", "App Rev Booster")}
            </span>
          </div>
        </div>

        {status === "verifying" && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 rounded-full flex items-center justify-center bg-indigo-50">
                <Spinner size={48} className="animate-spin text-indigo-500" />
              </div>
            </div>
            <div className="text-center mb-6 space-y-2">
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t("payment.verify.verifying.title", "Verifying your payment…")}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {t(
                  "payment.verify.verifying.description",
                  "We're confirming your subscription with our system. This should only take a moment."
                )}
              </p>
            </div>
          </>
        )}

        {status === "success" && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 rounded-full flex items-center justify-center bg-green-50">
                <CheckCircle size={48} weight="fill" className="text-green-500" />
              </div>
            </div>
            <div className="text-center mb-8 space-y-2">
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t("payment.verify.success.title", "Payment Successful!")}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {t(
                  "payment.verify.success.description",
                  "Your subscription is now active. You're all set to get started."
                )}
              </p>
            </div>
            <div className="space-y-3">
              <p className="text-center text-sm text-gray-400">
                {t("payment.verify.success.countdown", "Redirecting in {{count}}s…", {
                  count: countdown,
                })}
              </p>
              <button
                onClick={() => navigate("/dashboard", { replace: true })}
                className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {t("payment.verify.success.goNow", "Go to Dashboard")}
              </button>
            </div>
          </>
        )}

        {status === "processing" && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 rounded-full flex items-center justify-center bg-yellow-50">
                <Clock size={48} weight="fill" className="text-yellow-500" />
              </div>
            </div>
            <div className="text-center mb-8 space-y-3">
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t("payment.verify.processing.title", "Payment Received — Activating Your Plan")}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {t(
                  "payment.verify.processing.description",
                  "Your payment was successfully processed by Stripe. We're finalising the activation on our end — this usually takes less than a minute."
                )}
              </p>
              <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
                <p className="text-sm text-yellow-800 leading-relaxed">
                  {t(
                    "payment.verify.processing.assurance",
                    "If your plan isn't active within the next few minutes, don't worry — we'll either activate it automatically or issue a full refund to your original payment method. You'll receive an email confirmation either way."
                  )}
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/dashboard", { replace: true })}
                className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {t("payment.verify.goToDashboard", "Go to Dashboard")}
              </button>
              <button
                onClick={() => navigate("/billing", { replace: true })}
                className="w-full py-2.5 px-4 bg-white dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
              >
                {t("payment.verify.processing.checkBilling", "Check Billing Status")}
              </button>
            </div>
          </>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          {t(
            "payment.verify.support",
            "Need help? Contact us at support@apprevbooster.com"
          )}
        </p>
      </div>
    </div>
  );
};

export default PaymentVerify;
